package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Int
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(Reaction.Companion.Serializer::class)
public class Reaction : Resource, ReactionSchema {
  override val `companion`: Resource.CompanionObj<Reaction> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var reactableType: String
    get() = if (attributes.containsKey("reactable_type")) {
      attributes["reactable_type"] as String
    } else {
      throw NoSuchElementException("reactableType not found")
    }
    set(`value`) {
      attributes["reactable_type"] = value
    }

  override var reactableId: Int
    get() = if (attributes.containsKey("reactable_id")) {
      attributes["reactable_id"] as Int
    } else {
      throw NoSuchElementException("reactableId not found")
    }
    set(`value`) {
      attributes["reactable_id"] = value
    }

  override var reactionType: String
    get() = if (attributes.containsKey("type")) {
      attributes["type"] as String
    } else {
      throw NoSuchElementException("reactionType not found")
    }
    set(`value`) {
      attributes["type"] = value
    }

  override var userId: String?
    get() = if (attributes.containsKey("user_id")) {
      attributes["user_id"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["user_id"] = value
    }

  public constructor(
    reactableType: String? = null,
    reactableId: Int? = null,
    reactionType: String? = null,
    userId: String? = null,
  ) {
    if (reactableType != null) {
      attributes["reactable_type"] = reactableType
    }
    if (reactableId != null) {
      attributes["reactable_id"] = reactableId
    }
    if (reactionType != null) {
      attributes["type"] = reactionType
    }
    if (userId != null) {
      attributes["user_id"] = userId
    }
  }

  public companion object : Resource.CompanionObj<Reaction> {
    override val resourceType: String = "Reaction"

    override val endpoint: String = "reactions"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> Reaction = { Reaction() }

    override val schema: KClass<ReactionSchema> = ReactionSchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<Reaction>.() -> Unit = {}): Scope<Reaction> = Scope(Reaction::class, options)

    public final suspend fun all(): CollectionProxy<Reaction> = scope().all()

    public final suspend fun find(id: String): RecordProxy<Reaction> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<Reaction?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<Reaction> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<Reaction?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<Reaction> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<Reaction?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<Reaction> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<Reaction> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<Reaction> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<Reaction> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<Reaction> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<Reaction> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<Reaction> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<Reaction> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<Reaction> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<Reaction> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<Reaction>()
  }
}
